/**
 * Capability handshake for the Flutter native "ready" signal (CXO-2903).
 *
 * The native app opts in by appending `mobile_loaded_handshake=v1` to the
 * in-store checkout URL. Only then does PPUI emit `instore_checkout_loaded`.
 * Older app versions never send the param, so they never receive the signal
 * (which they would otherwise mishandle and close the webview).
 */

import { isMobileBridgeAvailable } from "@/lib/mobile";

const HANDSHAKE_PARAM = "mobile_loaded_handshake";
const SUPPORTED_VERSION = "v1";
/**
 * Set to `true` by the native app on the in-store (QR) checkout URL. Marks a
 * flow where the app has already authenticated the customer (checkout token in
 * cookies), so the handshake page skips the Tamara Auth login redirect.
 */
export const INSTORE_CHECKOUT_PARAM = "instore_checkout";
/** Session-scoped capability flag — survives the SPA rewriting its own URL. */
const CAPABILITY_STORAGE_KEY = "mobile_loaded_handshake_capability";
const EMITTED_STORAGE_PREFIX = "instore_ready_signal_sent:";

const readParamFromUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get(HANDSHAKE_PARAM);
  } catch {
    return null;
  }
};

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage can be unavailable (private mode / sandboxed webview) —
    // fall back to per-load behaviour rather than throwing.
  }
};

// Capture the capability once at module load, before the router can strip the
// query param, and persist it for the rest of the session.
const capabilityAtLoad = readParamFromUrl();
if (capabilityAtLoad) {
  writeSession(CAPABILITY_STORAGE_KEY, capabilityAtLoad);
}

/**
 * True only for the native in-store flow, requiring all of:
 *   1. `instore_checkout=true` on the URL — this is an in-store checkout,
 *   2. `mobile_loaded_handshake=v1` on the URL — the current native app's
 *      explicit opt-in (older apps omit it), and
 *   3. the runtime `Mobile` bridge — we are genuinely inside the native webview,
 *      not a browser that happens to carry the params.
 *
 * Only then does PPUI skip Tamara Auth (the app has already logged the customer
 * in and set the checkout token cookie). A plain mobile browser that scans the
 * QR (no app), an older app, or a stray URL in a browser all fall through to
 * the normal Auth login flow.
 *
 * Params are read from the live URL (not the persisted capability): this is an
 * entry-time decision on the handshake page, where the params are still intact.
 */
export const isNativeInstoreHandshake = (): boolean => {
  if (!isMobileBridgeAvailable()) {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get(INSTORE_CHECKOUT_PARAM) === "true" &&
      params.get(HANDSHAKE_PARAM) === SUPPORTED_VERSION
    );
  } catch {
    return false;
  }
};

/** True when the hosting native app advertised support for the ready signal. */
export const mobileSupportsReadySignal = (): boolean => {
  const version = readParamFromUrl() ?? readSession(CAPABILITY_STORAGE_KEY);
  return version === SUPPORTED_VERSION;
};

/** Whether the ready signal was already emitted for this checkout session. */
export const hasEmittedReadySignal = (checkoutId: string): boolean =>
  readSession(`${EMITTED_STORAGE_PREFIX}${checkoutId}`) === "1";

/** Marks the ready signal as emitted so it is not sent again this session. */
export const markReadySignalEmitted = (checkoutId: string): void => {
  writeSession(`${EMITTED_STORAGE_PREFIX}${checkoutId}`, "1");
};
